<script lang="ts">
    import type { Field, Lead } from '@/types/types';
    import type Blocks from '@/schemas/public/Blocks';
    import type { DependencyMap } from '@/types/fieldDependency';
    import { Button } from '@/components/ui/button';
    import { X, Loader2 } from 'lucide-svelte';
    import Input from '@/components/Form/Inputs/Input.svelte';
    import Textarea from '@/components/Form/Inputs/Textarea.svelte';
    import SiteSurveyForm from '@/components/leads/SiteSurveyForm.svelte';
    import { toast } from 'svelte-sonner';
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import DateOnly from '@/components/Inputs/Date.svelte';
    import { createDateDurationSync } from '@/lib/dateDurationCalculator.svelte';
    import { isWeekendDate } from '@/lib/dateUtils';

    const WEEKEND_WARN_FIELDS = new Set(['estimated_job_start', 'estimated_job_end']);
    import AddressLookup from '@/components/Inputs/AddressLookup.svelte';
    import { transformToNestedStructure } from '@/lib/formTransformers';
    import { Dialog } from "bits-ui";
    import Checkbox from '@/components/Inputs/Checkbox.svelte';
    import RelatedSingle from '@/components/Inputs/RelatedSingle.svelte';
    import CustomerTable from '@/components/customers/CustomerTable.svelte';
    import PhasesList from '@/components/phases/PhasesList.svelte';
    import {
        requiredFieldsByStatus,
        optionalFieldsByStatus,
        addressFieldNames,
        statusesThatRequireSiteSurvey,
        statusesThatShowPhases
    } from '@/lib/leadStatusRules';

    type Props = {
        lead: Lead;
        newStatus: string;
        statusLabel: string;
        show: boolean;
        blocks: (Blocks & { fields: Field[] })[];
        siteSurveyBlocks?: (Blocks & { fields: Field[] })[];
        siteSurveyDependencyMap?: DependencyMap;
        errors?: Record<string, string>;
        processing?: boolean;
        onConfirm: (data: Record<string, any>) => void;
        onCancel: () => void;
    };

    let { lead, newStatus, statusLabel, show = $bindable(), blocks, siteSurveyBlocks = [], siteSurveyDependencyMap = {}, errors = {}, processing = false, onConfirm, onCancel }: Props = $props();

    // Local copy of lead — updated when SiteSurveyForm saves, so isSiteSurveyComplete reflects saved state
    let localLead = $state(lead);

    // Get the fields we need to show for this status
    const requiredFieldNames = requiredFieldsByStatus[newStatus] || [];
    const optionalFieldNames = optionalFieldsByStatus[newStatus] || [];
    const allFieldNames = [...requiredFieldNames, ...optionalFieldNames];

    // Check if any required fields are missing
    const hasMissingFields = $derived(
        requiredFieldNames.some(fieldName => {
            // Check address fields in nested address object
            if (addressFieldNames.includes(fieldName)) {
                return !lead.address?.[fieldName as keyof typeof lead.address];
            }
            // Check is_primary from primaryCustomer relationship (camelCase from API)
            if (fieldName === 'is_primary') {
                return !lead.primaryCustomer?.id;
            }
            // Check other fields directly on lead
            return !lead[fieldName as keyof Lead];
        })
    );

    // Filter required fields from blocks
    const requiredFields = $derived(() => {
        const fields: Field[] = [];

        blocks.forEach(block => {
            block.fields.forEach(field => {
                if (requiredFieldNames.includes(field.column_name)) {
                    const fieldCopy = { ...field };

                    // Get value from nested address object for address fields
                    if (addressFieldNames.includes(field.column_name)) {
                        fieldCopy.value = lead.address?.[field.column_name as keyof typeof lead.address] || '';
                    } else if (field.column_name === 'is_primary') {
                        // Special handling for is_primary - get from primaryCustomer relationship (camelCase from API)
                        fieldCopy.value = lead.primaryCustomer?.id || '';
                    } else {
                        fieldCopy.value = lead[field.column_name as keyof Lead] || '';
                    }

                    fieldCopy.required = true;
                    fields.push(fieldCopy);
                }
            });
        });

        return fields;
    });

    // Filter optional fields from blocks
    const optionalFields = $derived(() => {
        const fields: Field[] = [];

        blocks.forEach(block => {
            block.fields.forEach(field => {
                if (optionalFieldNames.includes(field.column_name)) {
                    const fieldCopy = { ...field };

                    // Get value from nested address object for address fields
                    if (addressFieldNames.includes(field.column_name)) {
                        fieldCopy.value = lead.address?.[field.column_name as keyof typeof lead.address] || '';
                    } else {
                        fieldCopy.value = lead[field.column_name as keyof Lead] || '';
                    }

                    fieldCopy.required = false; // Optional fields are not required
                    fields.push(fieldCopy);
                }
            });
        });

        return fields;
    });

    let commentText = $state('');
    let datesConfirmed = $state(false);
    let datesConfirmCheckbox = $state<HTMLInputElement | null>(null);

    // Combine and sort fields by their original order in blocks
    const allFields = $derived(() => {
        const combined = [...requiredFields(), ...optionalFields()];

        // Sort by the order they appear in blocks
        combined.sort((a, b) => {
            let aIndex = -1;
            let bIndex = -1;
            let currentIndex = 0;

            for (const block of blocks) {
                for (const field of block.fields) {
                    if (field.id === a.id) aIndex = currentIndex;
                    if (field.id === b.id) bIndex = currentIndex;
                    currentIndex++;
                }
            }

            return aIndex - bIndex;
        });
        return combined;
    });

    // Initialize form data with lead values
    let formData = $state<Record<string, any>>({});
    let clientErrors = $state<Record<string, string>>({});
    let formDataInitialized = $state(false);

    $effect(() => {
        if (show && !formDataInitialized) {
            const newFormData: Record<string, any> = {};
            allFields().forEach(field => {
                let value = field.value || '';

                // Initialize checkboxes with boolean values
                if (field.ui_type === 'checkbox') {
                    value = field.value === true || field.value === 'true' || field.value === 1;
                }
                // Convert datetime strings to Date objects for DateTime component
                else if (field.ui_type === 'datetime' && value && typeof value === 'string') {
                    const date = new Date(value);
                    // Only set if it's a valid date
                    value = !isNaN(date.getTime()) ? date : null;
                }
                // Update primaryCustomerLabel when is_primary field is initialized
                else if (field.column_name === 'is_primary' && value) {
                    primaryCustomerLabel = lead.primaryCustomer?.name ?? null;
                }

                newFormData[field.column_name] = value;
            });

            // Initialize estimated_value for line-item statuses (may not be in allFields for invoiced)
            if ((newStatus === 'tendered' || newStatus === 'closed_won' || newStatus === 'invoiced') && !newFormData.estimated_value) {
                newFormData.estimated_value = lead.estimated_value ?? '';
            }

            // Default additional_hire_start_date to estimated_job_end for extra_hire
            if (newStatus === 'extra_hire' && !newFormData.additional_hire_start_date && lead.estimated_job_end) {
                const endDate = new Date(lead.estimated_job_end);
                if (!isNaN(endDate.getTime())) {
                    newFormData.additional_hire_start_date = endDate;
                }
            }

            formData = newFormData;
            formDataInitialized = true;
        } else if (!show) {
            formDataInitialized = false;
        }
    });

    // Calculate subtotal from lineItems
    const lineItemsSubtotal = $derived(() => {
        if (!lead.lineItems || lead.lineItems.length === 0) return 0;
        return lead.lineItems.reduce((sum, item) => sum + parseFloat(item.total?.toString() || '0'), 0);
    });

    // Sync lineItems subtotal with estimated_value for statuses that show line items
    $effect(() => {
        if ((newStatus === 'tendered' || newStatus === 'closed_won' || newStatus === 'invoiced') && lineItemsSubtotal() > 0) {
            formData.estimated_value = lineItemsSubtotal().toFixed(2);
        }
    });


    // Status guard — skip date calculations for statuses that don't show date fields
    const isDateCalcActive = () => newStatus !== 'needs_survey' && newStatus !== 'closed_lost' && newStatus !== 'new';

    // Estimated job date ↔ duration auto-calculation
    createDateDurationSync({
        getStartDate: () => formData.estimated_job_start,
        getEndDate: () => formData.estimated_job_end,
        getDuration: () => formData.estimated_hire_duration_days,
        setEndDate: (date) => { formData.estimated_job_end = date; },
        setDuration: (duration) => { formData.estimated_hire_duration_days = duration; },
        isActive: isDateCalcActive,
    });

    // Additional hire date ↔ duration auto-calculation
    createDateDurationSync({
        getStartDate: () => formData.additional_hire_start_date,
        getEndDate: () => formData.additional_hire_end_date,
        getDuration: () => formData.additional_hire_duration?.toString(),
        setEndDate: (date) => { formData.additional_hire_end_date = date; },
        setDuration: (duration) => { formData.additional_hire_duration = duration; },
        isActive: isDateCalcActive,
    });

    // Validate required fields
    function validateFields(): boolean {
        const newErrors: Record<string, string> = {};
        let isValid = true;

        // Check if we need to validate (only if we're showing missing fields)
        if (hasMissingFields) {
            allFields().forEach(field => {
                const value = formData[field.column_name];

                // Check if field is required and empty
                if (field.required && (!value || value.toString().trim() === '')) {
                    newErrors[field.column_name] = `${field.label} is required`;
                    isValid = false;
                }
            });
        }

        clientErrors = newErrors;
        return isValid;
    }

    function handleSubmit() {
        // Clear previous errors
        clientErrors = {};

        // Validate fields
        if (!validateFields()) {
            Object.values(clientErrors).forEach((message) => {
                toast.error(message);
            })
            return;
        }

        // Validate primary customer if required
        if (requiresPrimaryCustomer && !lead.primaryCustomer && !formData.is_primary) {
            toast.error('A primary customer is required when closing a lead as won.');
            return;
        }

        // Validate site survey is completed when required
        if ((newStatus === 'booked' || lead.status === 'needs_survey') && !isSiteSurveyComplete()) {
            toast.error('A completed site survey is required before proceeding.');
            return;
        }

        // Validate date confirmation for booked status
        if (newStatus === 'booked' && !datesConfirmed) {
            toast.error('Please confirm the job start and end dates before proceeding.');
            datesConfirmCheckbox?.focus();
            return;
        }

        // Send both required and optional fields (but not all formData)
        // This prevents overwriting site survey data that was just saved
        const dataToSend: Record<string, any> = {};
        allFieldNames.forEach(fieldName => {
            const field = allFields().find(f => f.column_name === fieldName);
            const value = formData[fieldName];

            // For checkbox fields, convert null/undefined to false
            if (field?.ui_type === 'checkbox') {
                dataToSend[fieldName] = value === true ? true : false;
            }
            // For other fields, only include if not null/undefined/empty
            else if (value !== undefined && value !== null && value !== '') {
                dataToSend[fieldName] = value;
            }
        });
        // Always include estimated_value for line-item statuses
        if ((newStatus === 'tendered' || newStatus === 'closed_won' || newStatus === 'invoiced') && formData.estimated_value) {
            dataToSend.estimated_value = formData.estimated_value;
        }

        const transformedData = transformToNestedStructure(dataToSend);
        if (commentText.trim()) {
            transformedData._comment = commentText.trim();
        }
        transformedData.status = newStatus;

        // Include is_primary if a new primary customer was selected
        if (formData.is_primary) {
            transformedData.is_primary = formData.is_primary;
        }

        onConfirm(transformedData);
        commentText = '';
    }

    function handleCancel() {
        formData = {};
        clientErrors = {};
        commentText = '';
        datesConfirmed = false;
        onCancel();
    }

    // Combine client-side and server-side errors
    const allErrors = $derived(() => {
        return { ...clientErrors, ...errors };
    });

    // Initial validation when modal opens - highlight empty required fields
    $effect(() => {
        if (show && hasMissingFields) {
            const initialErrors: Record<string, string> = {};

            allFields().forEach(field => {
                const value = formData[field.column_name];

                if (field.required && (!value || value.toString().trim() === '')) {
                    initialErrors[field.column_name] = `${field.label} is required`;
                }
            });

            clientErrors = initialErrors;
        } else if (!show) {
            // Clear errors when modal closes
            clientErrors = {};
        }
    });

    // Check if we're showing address fields
    const hasAddressFields = $derived(
        requiredFieldNames.includes('address_line_one') ||
        requiredFieldNames.includes('city') ||
        requiredFieldNames.includes('postcode')
    );

    // Handle address selection from lookup
    function handleAddressSelect(address: {
        line_1: string;
        line_2: string;
        city: string;
        postcode: string;
        county: string;
    }) {
        formData.address_line_one = address.line_1;
        formData.address_line_two = address.line_2; // Populate optional field
        formData.city = address.city;
        formData.postcode = address.postcode;
        formData.county = address.county;

        // Clear validation errors for these fields
        if (clientErrors.address_line_one) delete clientErrors.address_line_one;
        if (clientErrors.city) delete clientErrors.city;
        if (clientErrors.postcode) delete clientErrors.postcode;
    }

    // Site Survey validation and data
    // Survey is required when moving OUT of needs_survey or INTO booked
    const requiresSiteSurvey = $derived(
        statusesThatRequireSiteSurvey.includes(newStatus) || lead.status === 'needs_survey'
    );

    const isSiteSurveyComplete = $derived(() => {
        // Moving out of needs_survey or into booked always requires a completed survey
        if (newStatus === 'booked' || lead.status === 'needs_survey') return !!localLead.has_site_survey;
        // For other statuses, if survey is not required for this lead, consider it complete
        if (!localLead.survey_required) return true;
        return !!localLead.has_site_survey;
    });

    // Show the site survey form when moving out of needs_survey, into booked, or when survey_required is set
    const shouldShowSiteSurveyForm = $derived(
        requiresSiteSurvey && (lead.survey_required || newStatus === 'booked' || lead.status === 'needs_survey')
    );


    // Primary customer picker state
    let isPrimaryCustomerOpen = $state(false);
    let primaryCustomerLabel = $state<string | null>(lead.primaryCustomer?.name ?? null);

    // Check if primary customer is required for this status
    const requiresPrimaryCustomer = $derived(newStatus === 'closed_won');

    // Show PhasesList for needs_pricing onwards
    const shouldShowPhases = $derived(statusesThatShowPhases.includes(newStatus));
    const leadPhases = $derived(localLead.phases ?? []);

</script>

<Dialog.Root bind:open={show}>
    <Dialog.Portal>
        <Dialog.Overlay class="dialog-overlay" />
        <Dialog.Content
            class="dialog-content"
            trapFocus={!shouldShowPhases}
            onFocusOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
        >
            <div class="dialog-header">
                <Dialog.Title class="dialog-title">
                    Move to {statusLabel}
                </Dialog.Title>
                <Dialog.Close class="dialog-close">
                    <X size={20} />
                </Dialog.Close>
            </div>

            <div class="dialog-body">
                <p class="text-sm text-muted-foreground mb-4">
                    Moving <strong>{lead.first_name} {lead.last_name} {lead.postcode}</strong> to <strong>{statusLabel}</strong>
                </p>

                <div class="space-y-4">
                    <!-- Show current end date when moving to extra hire -->
                    {#if newStatus === 'extra_hire' && lead.estimated_job_end}
                        <div class="current-end-date-info">
                            <span class="text-sm text-muted-foreground">Current Hire End Date:</span>
                            <strong class="text-sm">{new Date(lead.estimated_job_end).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</strong>
                        </div>
                    {/if}

                    <!-- Dynamic fields based on status (in correct order) -->
                    {#if allFields().length > 0 && formDataInitialized}
                        {#if hasMissingFields}
                            <p class="text-sm text-muted-foreground mb-4">
                                Please complete the required information:
                            </p>
                        {/if}

                        <!-- Show address lookup if we have address fields -->
                        {#if hasAddressFields}
                            <div class="address-lookup-section">
                                <AddressLookup onAddressSelect={handleAddressSelect} />
                            </div>
                        {/if}

                        <!-- All fields (required and optional) in correct order -->
                        {#each allFields() as field (field.id)}
                            {#if field.ui_type === 'date'}
                                <DateOnly
                                    label={field.label}
                                    bind:value={formData[field.column_name]}
                                    error={allErrors()[field.column_name]}
                                    showValidation={field.required}
                                />
                                {#if WEEKEND_WARN_FIELDS.has(field.column_name) && isWeekendDate(formData[field.column_name])}
                                    <p class="-mt-2 mb-2 text-sm" style="color: var(--warning);" role="alert">
                                        Heads up — this falls on a weekend. You can still save it.
                                    </p>
                                {/if}
                            {:else if field.ui_type === 'datetime'}
                                <DateTime
                                    label={field.label}
                                    bind:value={formData[field.column_name]}
                                    error={allErrors()[field.column_name]}
                                    showValidation={field.required}
                                />
                                {#if WEEKEND_WARN_FIELDS.has(field.column_name) && isWeekendDate(formData[field.column_name])}
                                    <p class="-mt-2 mb-2 text-sm" style="color: var(--warning);" role="alert">
                                        Heads up — this falls on a weekend. You can still save it.
                                    </p>
                                {/if}
                            {:else if field.ui_type === 'checkbox'}
                                <Checkbox
                                    bind:checked={formData[field.column_name]}
                                    label={field.label}
                                    error={allErrors()[field.column_name]}
                                />
                            {:else if field.ui_type === 'related'}
                                <RelatedSingle
                                    label="Select Primary Customer"
                                    bind:value={formData[field.column_name]}
                                    error={allErrors()[field.column_name]}
                                    bind:isOpen={isPrimaryCustomerOpen}
                                    bind:valLabel={primaryCustomerLabel}
                                    showValidation={field.required}
                                >
                                    <CustomerTable
                                        view="related"
                                        onRowClick={(row) => {
                                            formData.is_primary = row?.id ?? null;
                                            primaryCustomerLabel = row?.name ?? '';
                                            isPrimaryCustomerOpen = false;

                                            // Auto-populate lead fields from the selected customer
                                            if (row) {
                                                formData.first_name = row.first_name ?? '';
                                                formData.last_name = row.last_name ?? '';
                                                formData.email = row.email ?? '';
                                                formData.phone = row.phone ?? '';
                                                formData.organisation_name = row.organisation_name ?? '';
                                            }

                                            // Clear validation error
                                            if (clientErrors.is_primary) delete clientErrors.is_primary;
                                        }}
                                    />
                                </RelatedSingle>
                            {:else}
                                <Input
                                    bind:value={formData[field.column_name]}
                                    {field}
                                    error={allErrors()[field.column_name]}
                                    onkeydown={null}
                                    showValidation={field.required}
                                />
                            {/if}
                        {/each}
                    {/if}

                    <!-- Date confirmation for booked status -->
                    {#if newStatus === 'booked' && formDataInitialized}
                        <div class="dates-confirm-box">
                            <label class="dates-confirm-label">
                                <input
                                    type="checkbox"
                                    bind:checked={datesConfirmed}
                                    bind:this={datesConfirmCheckbox}
                                    class="dates-confirm-checkbox"
                                />
                                <span>I confirm the job start and end dates above are correct</span>
                            </label>
                        </div>
                    {/if}

                    <!-- Site Survey Section -->
                    {#if shouldShowSiteSurveyForm && siteSurveyBlocks && siteSurveyBlocks.length > 0}
                        <SiteSurveyForm
                            record={localLead}
                            recordType="lead"
                            siteSurveyBlocks={siteSurveyBlocks}
                            siteSurveyDependencyMap={siteSurveyDependencyMap}
                            onRecordUpdated={(updated) => { localLead = updated; }}
                            compact={true}
                            incomplete={!isSiteSurveyComplete()}
                        />
                    {/if}

                    <!-- Phases (needs_pricing onwards) -->
                    {#if shouldShowPhases}
                        <PhasesList
                            recordType="lead"
                            recordId={localLead.id}
                            phases={leadPhases}
                            onRecordUpdated={(updated) => { localLead = updated; }}
                            inlineModals={true}
                        />
                    {/if}

                    <!-- Comment field (always shown, optional) -->
                    <Textarea
                        bind:value={commentText}
                        field={{
                            label: "Add a comment about this status change (optional)",
                            column_name: "comment",
                            ui_type: "textarea",
                            required: false
                        }}
                        error={null}
                        onkeydown={null}
                        className="m-0"
                    />
                </div>
            </div>

            <div class="dialog-footer">
                <Button variant="outline" onclick={handleCancel} disabled={processing}>
                    Cancel
                </Button>
                <Button onclick={handleSubmit} disabled={processing}>
                    {#if processing}
                        <Loader2 class="h-4 w-4 animate-spin mr-2" />
                        Processing...
                    {:else}
                        Confirm Move
                    {/if}
                </Button>
            </div>
        </Dialog.Content>
    </Dialog.Portal>
</Dialog.Root>


<style lang="scss">
    :global(.dialog-overlay) {
        position: fixed;
        inset: 0;
        z-index: 50;
        background: rgba(0, 0, 0, 0.5);
    }

    :global(.dialog-content) {
        position: fixed;
        left: 50%;
        top: 50%;
        z-index: 50;
        transform: translate(-50%, -50%);
        width: 90%;
        max-width: min(90vw, 896px);
        background: var(--clr-surface-1);
        border: 1px solid var(--clr-border);
        border-radius: 8px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
        max-height: 90vh;
        overflow-y: auto;
    }

    .dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;;
        border-bottom: 1px solid var(--clr-border);
    }

    :global(.dialog-title) {
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--clr-on-surface-1);
        margin: 0;
    }

    :global(.dialog-close) {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        border-radius: 4px;
        border: none;
        background: transparent;
        color: var(--clr-on-surface-2);
        cursor: pointer;
        transition: background 200ms;

        &:hover {
            background: var(--clr-surface-3);
        }
    }

    .dialog-body {
        padding: 1rem 1.5rem;
        padding-top: 0;
    }

    .dialog-footer {
        display: flex;
        justify-content: flex-end;
        gap: 0.75rem;
        padding: 1rem 1.5rem;
        border-top: 1px solid var(--clr-border);
    }

    .form-group {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .label {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--clr-on-surface-1);
    }

    .textarea {
        width: 100%;
        padding: 0.5rem 0.75rem;
        background: var(--clr-surface-2);
        border: 1px solid var(--clr-border);
        border-radius: 6px;
        color: var(--clr-on-surface-1);
        font-size: 0.875rem;
        transition: border-color 200ms;
        resize: vertical;
        min-height: 100px;

        &:focus {
            outline: none;
            border-color: var(--clr-primary);
        }

        &::placeholder {
            color: var(--clr-on-surface-3);
        }
    }

    .space-y-4 > * + * {
        margin-top: 1rem;
    }

    :global(.text-sm) {
        font-size: 0.875rem;
    }

    :global(.text-muted-foreground) {
        color: var(--clr-on-surface-2);
    }

    :global(.mb-4) {
        margin-bottom: 1rem;
    }

    :global(.mt-2) {
        margin-top: 0.5rem;
    }

    .current-end-date-info {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        background: hsl(var(--clr-primary) / 0.1);
        border: 1px solid hsl(var(--clr-primary) / 0.25);
        border-radius: 6px;
    }

    .line-items-section {
        margin-bottom: 1.5rem;
    }

    .address-lookup-section {
        margin-bottom: 1rem;
    }

    .dates-confirm-box {
        padding: 0.75rem 1rem;
        background: hsl(var(--warning) / 0.08);
        border: 1px solid hsl(var(--warning) / 0.3);
        border-radius: 6px;
    }

    .dates-confirm-label {
        display: flex;
        align-items: center;
        gap: 0.625rem;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--clr-on-surface-1);
        cursor: pointer;
    }

    .dates-confirm-checkbox {
        width: 1rem;
        height: 1rem;
        flex-shrink: 0;
        cursor: pointer;
        accent-color: var(--primary);
    }


    .primary-customer-section {
        background: var(--clr-surface-2);
        border-radius: 8px;
        padding: 1rem;
        border: 1px solid var(--clr-border);
        transition: all 200ms ease;

        &.complete {
            background: var(--success) / 0.05;
            border-color: var(--success);
        }
    }

    .section-heading {
        font-size: 1rem;
        font-weight: 600;
        color: var(--clr-on-surface-1);
        margin: 0 0 0.25rem 0;
    }

    .error-message {
        padding: 0.75rem;
        background: var(--destructive) / 0.1;
        border: 1px solid var(--destructive) / 0.3;
        border-radius: 6px;
        color: var(--destructive);
        font-size: 0.875rem;
        margin-bottom: 1rem;
    }

</style>
